import React, { useCallback, useEffect, useRef, useState } from 'react';
import CardShell from '../../components/CardShell';
import { Cell } from '../../components/InlineCells';
import { useP } from '../../i18n/useP';
import { getDebts, putDebtAccounts, putDebtRepay } from '../../api/financials_debts';
import './FinancingPlanPage.css';

/* EMPRUNTS ET APPORTS, MODE MANUEL (décision Olivier, 2026-10-03 ; proto
   `WIP/protos/emprunts-apports.html`). On retient les comptes de fonds
   étrangers qui portent une dette financière ; leur solde à la clôture fait le
   solde initial, et l'on tape les remboursements des mois ouverts. Ils baissent
   la dette sur la ligne du bilan qui porte le compte et sortent de la
   trésorerie au plan de liquidité. Tout se calcule au serveur. */

const MONTHS = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
const SECTIONS = [
  { code: 'current_liabilities', label: 'Dettes à court terme' },
  { code: 'long_term_liabilities', label: 'Dettes à long terme' },
];
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

export default function DebtManualCard({ activeTenant, year, unit = 'kchf', canWrite = true }) {
  const p = useP();
  const tid = activeTenant?.id;
  const k = unit === 'chf' ? 1 : 1000;
  const [v, setV] = useState(null);
  const [picking, setPicking] = useState(false);
  const [q, setQ] = useState('');
  const pickRef = useRef(null);

  const load = useCallback(() => {
    if (!tid) return;
    getDebts(tid, year).then((r) => setV(r && !r.error ? r.data : null)).catch(() => setV(null));
  }, [tid, year]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!picking) return undefined;
    const away = (e) => { if (pickRef.current && !pickRef.current.contains(e.target)) setPicking(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [picking]);

  const fmt = (x) => {
    const y = num(x) / k;
    if (Math.abs(y) < 0.005) return '—';
    return `${y < 0 ? '−' : ''}${Math.abs(y).toLocaleString('de-CH', { minimumFractionDigits: unit === 'chf' ? 0 : 1, maximumFractionDigits: unit === 'chf' ? 0 : 1 })}`;
  };
  /* ⚠️ UN MONTANT SE TAPE DANS L'UNITÉ DE L'ÉCRAN, comme partout. */
  const money = (x) => ({
    value: x, type: 'number',
    format: (y) => (y == null || Number(y) === 0 ? '' : fmt(y)),
    toDraft: (y) => (y == null || Number(y) === 0 ? '' : String(num(y) / k)),
  });

  const save = (pr) => pr.then((r) => { if (r && !r.error) setV(r.data); }).catch(() => {});
  const rows = v?.rows || [];
  const openMonths = v?.open_months || [];
  const cc = num(v?.closed_count);
  const picked = rows.map((r) => r.account);
  const setAccounts = (list) => save(putDebtAccounts(tid, year, list));
  const add = (acc) => { setPicking(false); setQ(''); setAccounts([...picked, acc]); };
  const remove = (acc) => setAccounts(picked.filter((a) => a !== acc));

  /* Le solde initial : au dernier mois clos de l'exercice, sinon au 31.12 de
     l'exercice précédent (rejoué depuis la clôture). */
  const openLabel = cc >= 1
    ? `${p('Solde au')} ${new Date(year, cc, 0).getDate()}.${String(cc).padStart(2, '0')}`
    : `${p('Solde au')} 31.12.${year - 1}`;

  // Quelques lignes : le total se refait à chaque rendu, sans mémo (une
  // dépendance recréée à chaque rendu le rendrait inutile).
  const totals = (() => {
    const open = rows.reduce((s, r) => s + num(r.opening), 0);
    const perMonth = Object.fromEntries(openMonths.map((m) => [m, rows.reduce((s, r) => s + num(r.repay?.[m]), 0)]));
    const paid = Object.values(perMonth).reduce((s, x) => s + x, 0);
    let run = open;
    const runs = Object.fromEntries(openMonths.map((m) => [m, (run -= perMonth[m])]));
    return { open, perMonth, paid, runs };
  })();

  const options = (v?.available || []).filter((a) => !q.trim()
    || `${a.account} ${a.name}`.toLowerCase().includes(q.trim().toLowerCase()));

  const picker = canWrite && (
    <span className="dmc-pickwrap" ref={pickRef}>
      <button type="button" className="fpl-new" onClick={() => setPicking((x) => !x)}>＋ {p('Compte')}</button>
      {picking && (
        <div className="dmc-pick" role="dialog" aria-label={p('Comptes de fonds étrangers')}>
          <input className="lk-filter-input dmc-pick__q" autoFocus value={q}
                 placeholder={p('Rechercher un compte')} onChange={(e) => setQ(e.target.value)} />
          <div className="dmc-pick__list">
            {SECTIONS.map((s) => {
              const list = options.filter((a) => a.section === s.code);
              if (!list.length) return null;
              return (
                <React.Fragment key={s.code}>
                  <div className="dmc-pick__g">{p(s.label)}</div>
                  {list.map((a) => (
                    <button key={a.account} type="button" className="dmc-pick__o" disabled={a.picked}
                            onClick={() => add(a.account)}>
                      <span className="dmc-no">{a.account}</span>
                      <span className="dmc-pick__n">{a.name || a.line_label}</span>
                      <span className="dmc-pick__v">{a.picked ? p('retenu') : fmt(a.opening)}</span>
                    </button>
                  ))}
                </React.Fragment>
              );
            })}
            {!options.length && <div className="dmc-pick__g">{p('Aucun compte')}</div>}
          </div>
        </div>
      )}
    </span>
  );

  const cols = 4 + openMonths.length;
  return (
    <CardShell className="report-card" title={p('Dettes financières')} primaryAction={picker}>
      <div className="lk-hscroll">
        <table className="lk-entry-table fpl-tbl dmc-tbl">
          <colgroup>
            <col style={{ width: '280px' }} />
            <col style={{ width: '104px' }} />
            {openMonths.map((m) => <col key={m} style={{ width: '84px' }} />)}
            <col style={{ width: '96px' }} />
            <col style={{ width: '110px' }} />
          </colgroup>
          <thead>
            <tr className="dmc-band">
              <th />
              <th className="dmc-band__c">{p('Clôture')}</th>
              {openMonths.length > 0 && <th className="dmc-band__c dmc-edge" colSpan={openMonths.length}>{p('Remboursements prévus')}</th>}
              <th className="dmc-band__c dmc-edge" colSpan={2}>{p('Exercice')} {year}</th>
            </tr>
            <tr>
              <th className="l">{p('Compte')}</th>
              <th>{openLabel}</th>
              {openMonths.map((m, i) => <th key={m} className={i === 0 ? 'dmc-edge' : ''}>{MONTHS[m - 1]}</th>)}
              <th className="dmc-edge">{p('Remboursé')}</th>
              <th>{p('Solde au')} 31.12</th>
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((s) => {
              const list = rows.filter((r) => r.section === s.code);
              if (!list.length) return null;
              return (
                <React.Fragment key={s.code}>
                  <tr className="dmc-grp"><td className="l" colSpan={cols}>{p(s.label)}</td></tr>
                  {list.map((r) => (
                    <tr key={r.account}>
                      <td className="l dmc-acc">
                        {canWrite && (
                          <button type="button" className="lk-x lk-del" aria-label={p('Retirer le compte')}
                                  onClick={() => remove(r.account)}>×</button>
                        )}
                        <span className="dmc-no">{r.account}</span>{r.name || r.line_label}
                      </td>
                      <td className="dmc-close">{fmt(r.opening)}</td>
                      {openMonths.map((m, i) => (
                        <td key={m} className={i === 0 ? 'dmc-edge' : ''}>
                          <Cell {...money(r.repay?.[m])} canWrite={canWrite} placeholder="—"
                                onSave={(x) => save(putDebtRepay(tid, year, r.account, m, x == null ? null : x * k))} />
                        </td>
                      ))}
                      <td className="dmc-edge">{fmt(r.paid)}</td>
                      <td className="dmc-strong">{fmt(r.closing)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            {rows.length > 0 && (
              <>
                <tr className="lk-hi-strong dmc-tot">
                  <td className="l">{p('Total dettes financières')}</td>
                  <td className="dmc-close">{fmt(totals.open)}</td>
                  {openMonths.map((m, i) => <td key={m} className={i === 0 ? 'dmc-edge' : ''}>{fmt(totals.perMonth[m])}</td>)}
                  <td className="dmc-edge">{fmt(totals.paid)}</td>
                  <td>{fmt(totals.open - totals.paid)}</td>
                </tr>
                <tr className="dmc-run">
                  <td className="l">{p('Dette au bilan, fin de mois')}</td>
                  <td className="dmc-close">{fmt(totals.open)}</td>
                  {openMonths.map((m, i) => <td key={m} className={i === 0 ? 'dmc-edge' : ''}>{fmt(totals.runs[m])}</td>)}
                  <td className="dmc-edge" /><td />
                </tr>
              </>
            )}
            {v && !rows.length && (
              <tr><td className="l fpl-ro" colSpan={cols}>{p('Aucun compte retenu.')}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {rows.length > 0 && (
        <div className="fpl-foot">
          <span>{p('Remboursements')} {year} <b>{fmt(totals.paid)}</b> → {p('flux de financement')}</span>
          <span>{p('Solde au')} 31.12 <b>{fmt(totals.open - totals.paid)}</b> → {p('bilan')}</span>
        </div>
      )}
    </CardShell>
  );
}
